/**
 * Interface language (ADR 0040). Portuguese is the product's language and the source of every string: the text in the
 * code is the pt-BR copy and also the catalog key; English is the one other catalog. The choice is fixed for the life
 * of the page (read once at startup from this browser; a browser that never chose follows the account), and changing it
 * reloads the page, so labels computed at module load are translated too. A missing English entry falls back to the
 * Portuguese text.
 */
export type Lang = 'pt-BR' | 'en'
const KEY = 'subsindico.lang'
/** The browser globals this module touches, typed here so server-side unit tests can import the SPA's label files. */
const env = globalThis as {
  localStorage?: { getItem(k: string): string | null; setItem(k: string, v: string): void }
  document?: { documentElement: { lang: string } }
  location?: { reload(): void }
}

/** The choice stored in this browser, or null when it never chose. */
function stored(): Lang | null {
  try {
    const v = env.document ? env.localStorage?.getItem(KEY) : null
    if (v === 'en' || v === 'pt-BR') return v
  } catch {
    // storage blocked: treat as never chosen
  }
  return null
}

export const lang: Lang = stored() ?? 'pt-BR'
/** The English catalog is its own chunk, fetched only when English is on: Portuguese readers never download it. */
const EN: Record<string, string> = lang === 'en' ? (await import('../i18n/en.ts')).EN : {}
/** BCP 47 locale for Intl. British English keeps the day-first dates and 24-hour clock Brazil uses (and the catalog's spelling); money stays BRL. */
export const LOCALE = lang === 'en' ? 'en-GB' : 'pt-BR'

if (env.document) env.document.documentElement.lang = LOCALE

/** Translate a pt-BR UI string; `{name}` placeholders are filled from `vars` in either language. */
export function t(pt: string, vars?: Record<string, string | number | null | undefined>): string {
  const s = lang === 'en' ? (EN[pt] ?? pt) : pt
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k] ?? '') : m)) : s
}

/** Store the choice and reload, so every label (including module-level ones) is rebuilt in the new language. */
export function setLang(next: Lang): void {
  try {
    env.localStorage?.setItem(KEY, next)
  } catch {
    // nothing to persist
  }
  if (next !== lang) env.location?.reload()
}

/**
 * Reconcile this browser with the account's saved language once `/me` loads. A browser that never chose starts in the
 * account's language (one reload at most); a browser that did choose wins, and the account learns that choice through
 * `save`, so English picked on the login page sticks and follows the person to the next device.
 */
export function adoptAccountLang(saved: string | null | undefined, save: (next: Lang) => void): void {
  if ((saved !== 'en' && saved !== 'pt-BR') || saved === lang) return
  if (stored() === null) setLang(saved)
  else save(lang)
}
